import { useEffect, useState } from "react";
import { useParams, Outlet, Link, useSearchParams } from "react-router-dom";
import { Lock, Archive } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import TaskModal from "@/components/TaskModal";

// Loads project context once and shares it with the operational pages
// (Backlog, Kanban, Sprints, Reports, Settings) via the router Outlet context.
// Hosts the single reusable TaskModal so it can be opened from any sub-page.
export default function ProjectShell() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [modal, setModal] = useState(null); // { taskId } | { taskId: null, defaultSprintId }
  const [searchParams, setSearchParams] = useSearchParams();

  const bump = () => setRefreshKey((k) => k + 1);

  const loadAux = async () => {
    try { const { data } = await api.get(`/projects/${id}/sprints`); setSprints(data.sprints || []); } catch { /* noop */ }
    try { const { data } = await api.get(`/projects/${id}/board`); setColumns(data.columns || []); } catch { /* noop */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      if (data.can_admin) {
        try { const m = await api.get(`/workspaces/${data.workspace_id}/members`); setMembers([...(m.data.managers || []), ...(m.data.members || [])]); } catch { /* noop */ }
      }
      await loadAux();
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { if (project) loadAux(); /* eslint-disable-next-line */ }, [refreshKey]);

  // Open a task modal automatically when navigated with ?task=<id> (from Dashboard etc.)
  useEffect(() => {
    const tid = searchParams.get("task");
    if (tid) {
      setModal({ taskId: tid });
      const next = new URLSearchParams(searchParams);
      next.delete("task");
      setSearchParams(next, { replace: true });
    }
    /* eslint-disable-next-line */
  }, [searchParams]);

  const openTask = (taskId) => setModal({ taskId });
  const createTask = (extra = {}) => setModal({ taskId: null, ...extra });

  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="project-error">{error}</div>;
  if (!project) return <div className="text-slate-500">Carregando projeto...</div>;

  const ctx = { projectId: id, project, members, sprints, columns, refreshKey, bump, reload: load, openTask, createTask };

  return (
    <div className="space-y-6" data-testid="project-shell">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/projetos" className="hover:text-slate-900">Projetos</Link>
        <span>/</span>
        <span className="font-medium text-slate-700 inline-flex items-center gap-1">
          {project.confidential && <Lock className="w-3 h-3 text-rose-500" />}
          {project.status === "archived" && <Archive className="w-3 h-3 text-slate-400" />}
          {project.name}
        </span>
      </div>

      <Outlet context={ctx} />

      {modal && (
        <TaskModal projectId={id} project={project} members={members} sprints={sprints}
          taskId={modal.taskId} defaultSprintId={modal.defaultSprintId} defaultColumnId={modal.defaultColumnId}
          onClose={() => setModal(null)} onSaved={bump} />
      )}
    </div>
  );
}
